/** @jsxImportSource @emotion/react */
import { PageHeader } from "../../../components/page-skeleton/PageHeader";
import { FormRow } from "../../common/hook-form";
import * as Inputs from "../../common/hook-form/index";
import { useFormContext } from "react-hook-form";
import { CardContainer } from "../../../components/page-skeleton/CardContainer";
import { Country } from "../../../model/Country";
import { CardBody } from "../../../components/page-skeleton/CardBody";
import { cardBottomPadding } from "../../../style-util";
import { useDeleteNews } from "../../../service/api";
import { DeleteButton } from "../../../components/NewButtons";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { getErrorMessage } from "../../../error-handler";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router";
import { routes } from "../../../appRoutingHelpers";

type Props = {
  countries: Country[];
};

export const NewsGeneralInformationSection = ({ countries }: Props) => {
  const { id } = useParams();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();
  const {
    register,
    formState: { errors: formErrors },
    clearErrors,
  } = useFormContext();

  const {
    mutateAsync: deleteNews,
    isLoading: isDeletingNews,
  } = useDeleteNews();

  const countryOptions = countries?.map((o) => ({
    label: o.countryCode,
    value: o.countryCode,
  }));

  countryOptions?.unshift({
    label: "",
    value: "",
  });

  return (
    <CardContainer>
      <PageHeader
        title="General news information"
        subtitle="Configure the general settings for this news."
      >
        <DeleteButton
          disabled={isDeletingNews}
          onClick={async () => {
            if (!id) {
              return;
            }

            try {
              await deleteNews({ organizationKeyName, data: { id } });
              toastr.success("Success", "News deleted successfully");
              navigate(routes.news(organizationKeyName));
            } catch (error: unknown) {
              const errorMessage = getErrorMessage(error);
              toastr.error("Error", errorMessage);
            }
          }}
        >
          Delete news
        </DeleteButton>
      </PageHeader>
      <CardBody css={cardBottomPadding}>
        <FormRow>
          <Inputs.InputFormField
            errors={formErrors}
            register={register}
            label="Title"
            name="title"
            helpText="The title of the news."
          />
          <Inputs.InputFormField
            errors={formErrors}
            register={register}
            label="Description"
            name="description"
            helpText="A short description of the news. We recommend max 20 words."
          />
        </FormRow>
        <FormRow>
          <Inputs.SelectFormField
            errors={formErrors}
            register={register}
            label="Country code"
            name="countryCode"
            helpText="Choose the country in which this news should be available."
            options={countryOptions}
            clearErrors={clearErrors}
          />
          <Inputs.InputFormField
            errors={formErrors}
            register={register}
            label="External URL"
            name="externalUrl"
            helpText="Where should this news link to once clicked?"
          />
        </FormRow>
      </CardBody>
    </CardContainer>
  );
};
